// Icon set ported from the prototype's icons.jsx.
// All icons are stroke-only, 1.5–2px, currentColor. Sized via the `s`
// prop. Defaults match the prototype.

type IconProps = { s?: number };

function I(props: { s: number; children: React.ReactNode; stroke?: number }) {
  return (
    <svg
      width={props.s}
      height={props.s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={props.stroke ?? 1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {props.children}
    </svg>
  );
}

export const Icon = {
  Home: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M3 11.5 12 4l9 7.5" />
      <path d="M5 10v9h14v-9" />
    </I>
  ),
  Store: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M3 9 5 4h14l2 5" />
      <path d="M3 9v11h18V9" />
      <path d="M9 20v-6h6v6" />
      <path d="M3 9c0 2 1.5 3 3 3s3-1 3-3c0 2 1.5 3 3 3s3-1 3-3c0 2 1.5 3 3 3s3-1 3-3" />
    </I>
  ),
  Inbox: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M3 13V6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" />
      <path d="M3 13h5l1.5 2.5h5L16 13h5v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Z" />
    </I>
  ),
  Sparkles: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M12 4v4M12 16v4M4 12h4M16 12h4" />
      <path d="m6 6 1.8 1.8M16.2 16.2 18 18M6 18l1.8-1.8M16.2 7.8 18 6" />
    </I>
  ),
  Users: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <circle cx="9" cy="9" r="3" />
      <path d="M3 19c0-3.3 2.7-6 6-6s6 2.7 6 6" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M15.5 13.5c2.7.5 4.5 2.7 4.5 5.5" />
    </I>
  ),
  Settings: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.5-2-3.5-2.4.9a7 7 0 0 0-2.1-1.2L14 3h-4l-.4 2.5a7 7 0 0 0-2.1 1.2L5.1 5.8 3.1 9.3l2 1.5A7 7 0 0 0 5 12c0 .4 0 .8.1 1.2l-2 1.5 2 3.5 2.4-.9a7 7 0 0 0 2.1 1.2L10 21h4l.4-2.5a7 7 0 0 0 2.1-1.2l2.4.9 2-3.5-2-1.5c.1-.4.1-.8.1-1.2Z" />
    </I>
  ),
  Search: ({ s = 16 }: IconProps) => (
    <I s={s} stroke={1.7}>
      <circle cx="11" cy="11" r="6" />
      <path d="m20 20-4.3-4.3" />
    </I>
  ),
  Bell: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M18 16V11a6 6 0 1 0-12 0v5l-2 3h16Z" />
      <path d="M10 21a2 2 0 0 0 4 0" />
    </I>
  ),
  // ADR-0153 — encuestas del propietario: la burbuja con una línea del
  // ícono `iEnc` de la PWA.
  Survey: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M9 10h6" />
    </I>
  ),
  Help: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 1-1 1.7" />
      <circle cx="12" cy="17" r=".6" fill="currentColor" />
    </I>
  ),
  ChevronRight: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="m9 6 6 6-6 6" />
    </I>
  ),
  ChevronDown: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="m6 9 6 6 6-6" />
    </I>
  ),
  ChevronLeft: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="m15 6-6 6 6 6" />
    </I>
  ),
  Arrow: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={1.8}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </I>
  ),
  Plus: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={1.8}>
      <path d="M12 5v14M5 12h14" />
    </I>
  ),
  Phone: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </I>
  ),
  Close: ({ s = 18 }: IconProps) => (
    <I s={s} stroke={1.8}>
      <path d="M6 6 18 18M6 18 18 6" />
    </I>
  ),
  Send: ({ s = 16 }: IconProps) => (
    <I s={s} stroke={1.8}>
      <path d="M4 12 20 4l-3 16-4-7-9-1Z" />
    </I>
  ),
  TrendUp: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="m4 17 7-7 4 4 5-5" />
      <path d="M15 9h5v5" />
    </I>
  ),
  TrendDown: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="m4 7 7 7 4-4 5 5" />
      <path d="M15 15h5v-5" />
    </I>
  ),
  Flat: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={2}>
      <path d="M5 12h14" />
    </I>
  ),
  Calendar: ({ s = 16 }: IconProps) => (
    <I s={s}>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </I>
  ),
  // «Comparar tiendas» action chip (ADR-0167): a grid, not a question.
  Table: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 4v16" />
    </I>
  ),
  MoreHoriz: ({ s = 18 }: IconProps) => (
    <I s={s} stroke={2}>
      <circle cx="6" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="18" cy="12" r="1" />
    </I>
  ),
  LogOut: ({ s = 16 }: IconProps) => (
    <I s={s}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </I>
  ),
  Download: ({ s = 14 }: IconProps) => (
    <I s={s} stroke={1.7}>
      <path d="M12 4v12M7 11l5 5 5-5" />
      <path d="M5 20h14" />
    </I>
  ),
  Filter: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <path d="M4 5h16l-6 8v6l-4-2v-4L4 5Z" />
    </I>
  ),
  Map: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z" />
      <path d="M9 4v14M15 6v14" />
    </I>
  ),
  Mail: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </I>
  ),
  External: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={1.7}>
      <path d="M14 5h5v5M19 5l-9 9M11 5H6a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-5" />
    </I>
  ),
  Photo: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M21 17 16 12l-9 7" />
    </I>
  ),
  Mic: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
      <path d="M12 18v3M9 21h6" />
    </I>
  ),
  Pin: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <path d="M12 21s7-6.3 7-12a7 7 0 1 0-14 0c0 5.7 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.5" />
    </I>
  ),
  Database: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6" />
      <path d="M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </I>
  ),
  Tag: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="m4 13 8 8 9-9V4h-8L4 13Z" />
      <circle cx="9" cy="9" r="1.5" />
    </I>
  ),
  Check: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={2.2}>
      <path d="m4 12 5 5L20 6" />
    </I>
  ),
  Clock: ({ s = 12 }: IconProps) => (
    <I s={s} stroke={1.8}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </I>
  ),
  Warning: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M12 3.5 21.5 20h-19L12 3.5Z" />
      <path d="M12 9.5v5" />
      <circle cx="12" cy="17.2" r=".6" fill="currentColor" />
    </I>
  ),
  // "Por revisar" marker (gate de confianza): always paired with the
  // golden --review-pending-* tokens and a text label, never alone.
  Eye: ({ s = 14 }: IconProps) => (
    <I s={s}>
      <path d="M2 12s3.7-7 10-7 10 7 10 7-3.7 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </I>
  ),
  // Postventa v2 sidebar: "Unidades del proyecto" (a building facade).
  Units: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <rect x="4" y="3" width="16" height="18" rx="1.5" />
      <path d="M9 7h1M14 7h1M9 11h1M14 11h1M9 15h1M14 15h1" />
    </I>
  ),
  // Postventa v2: archived / closed observations.
  Archive: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <rect x="3" y="4" width="18" height="4" rx="1" />
      <path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8M10 12h4" />
    </I>
  ),
  // ADR-0144 §4 — documentos del proyecto (path del handoff canal único, `P.folder`).
  Folder: ({ s = 18 }: IconProps) => (
    <I s={s}>
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </I>
  ),
} as const;

// Sparkline. CSS-driven coloring via the .spark-svg / .spark-line /
// .spark-area classes already in dashboard.css.
export function Spark({
  data,
  tone = "brand",
  w = 84,
  h = 28,
}: {
  data: number[] | null | undefined;
  tone?: "brand" | "ok" | "crit";
  w?: number;
  h?: number;
}) {
  if (!data || data.length < 2) {
    return <div className="spark-empty">sin datos</div>;
  }
  const min = Math.min(...data) - 0.05;
  const max = Math.max(...data) + 0.05;
  const range = Math.max(max - min, 0.01);
  const stepX = w / (data.length - 1);
  const pts = data.map((v, i) => [i * stepX, h - ((v - min) / range) * h] as const);
  const linePath = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(" ");
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const toneSuffix = tone === "brand" ? "" : `--${tone}`;
  return (
    <svg className="spark-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={areaPath} className={`spark-area${toneSuffix ? ` spark-area${toneSuffix}` : ""}`} />
      <path d={linePath} className={`spark-line${toneSuffix ? ` spark-line${toneSuffix}` : ""}`} />
    </svg>
  );
}
